import React, { useState } from "react";
import { db } from "../firebase";
import {
  doc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { useVenue } from "./VenueProvider";
import { useAuth } from "./AuthProvider";
import { Alert } from "./alert";
import { Button } from "./button";
import { showAlert } from "../alertService";
import { esSoloLectura } from "../utils/roles";
import { PlusIcon, TrashIcon } from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const BUILTIN = [
  { value: "cotizacion", label: "Cotización" },
  { value: "contrato", label: "Contrato" },
  { value: "comprobante", label: "Comprobante" },
  { value: "recibo", label: "Recibo" },
  { value: "otro", label: "Otro" },
];

const ArchivosTiposManager = () => {
  const { selectedVenueId, archivosTipos, archivosTiposLoading } = useVenue();
  const { user, userInfo } = useAuth();
  // Inversionista: ve los tipos, no los gestiona (P-FRA-13).
  const soloLectura = esSoloLectura(userInfo);
  const [newTipo, setNewTipo] = useState("");
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [busy, setBusy] = useState(false);

  const builtinLower = new Set(BUILTIN.map((t) => t.label.toLowerCase()));

  const handleAdd = async () => {
    const trimmed = newTipo.trim();
    if (!trimmed || !selectedVenueId) return;
    if (builtinLower.has(trimmed.toLowerCase())) {
      showAlert("Ese tipo ya existe como predeterminado.", "warning");
      return;
    }
    if (archivosTipos.some((t) => t.toLowerCase() === trimmed.toLowerCase())) {
      showAlert("Ese tipo ya está en la lista.", "warning");
      return;
    }
    setBusy(true);
    try {
      const ref = doc(
        db,
        "venues",
        selectedVenueId,
        "settings",
        "archivosTipos",
      );
      await setDoc(
        ref,
        {
          tipos: arrayUnion(trimmed),
          updatedAt: new Date(),
          updatedBy: user?.uid || null,
        },
        { merge: true },
      );
      setNewTipo("");
    } catch (err) {
      console.error("Error adding archivo tipo:", err);
      showAlert("No se pudo agregar el tipo.", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteCandidate || !selectedVenueId) {
      setDeleteCandidate(null);
      return;
    }
    setBusy(true);
    try {
      const ref = doc(
        db,
        "venues",
        selectedVenueId,
        "settings",
        "archivosTipos",
      );
      await updateDoc(ref, { tipos: arrayRemove(deleteCandidate) });
    } catch (err) {
      console.error("Error deleting archivo tipo:", err);
      showAlert("No se pudo borrar el tipo.", "error");
    } finally {
      setBusy(false);
      setDeleteCandidate(null);
    }
  };

  return (
    <div className="mt-8">
      <h2 className="text-lg font-semibold text-zinc-900 dark:text-white mb-1">
        Tipos de archivo
      </h2>
      <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
        {soloLectura
          ? "Tipos con los que se categorizan los archivos de los eventos de este venue."
          : "Cuando subes un archivo en un evento puedes categorizarlo. Aquí gestionas los tipos propios de tu venue, además de los predeterminados."}
      </p>

      {!soloLectura && (
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-4 shadow-sm mb-4">
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
              Nuevo tipo
            </label>
            <input
              type="text"
              className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
              value={newTipo}
              onChange={(e) => setNewTipo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdd();
              }}
              placeholder="Ej. Factura, Voucher de transferencia, Acta..."
              disabled={busy}
            />
          </div>
          <div>
            <button
              type="button"
              onClick={handleAdd}
              disabled={busy || !newTipo.trim()}
              className="flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: ACCENT }}
            >
              <PlusIcon className="h-4 w-4" />
              Agregar
            </button>
          </div>
        </div>
      </div>
      )}

      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm overflow-hidden">
        {archivosTiposLoading ? (
          <div className="text-center py-10 text-zinc-400">Cargando...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 dark:border-zinc-700/60">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    Tipo
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 w-[120px]">
                    Origen
                  </th>
                  <th className="px-4 py-2.5 w-[80px]"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {BUILTIN.map((t) => (
                  <tr key={`builtin-${t.value}`}>
                    <td className="px-4 py-2.5 text-zinc-700 dark:text-zinc-200">
                      {t.label}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-zinc-400">
                      Predeterminado
                    </td>
                    <td className="px-4 py-2.5"></td>
                  </tr>
                ))}
                {archivosTipos
                  .slice()
                  .sort((a, b) =>
                    a.localeCompare(b, "es", { sensitivity: "base" }),
                  )
                  .map((t) => (
                    <tr key={`custom-${t}`}>
                      <td className="px-4 py-2.5 text-zinc-700 dark:text-zinc-200">
                        {t}
                      </td>
                      <td
                        className="px-4 py-2.5 text-xs"
                        style={{ color: ACCENT }}
                      >
                        Personalizado
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {!soloLectura && (
                        <button
                          type="button"
                          onClick={() => setDeleteCandidate(t)}
                          className="text-zinc-400 hover:text-red-600 p-1.5"
                          title="Borrar"
                          aria-label="Borrar tipo"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Alert open={!!deleteCandidate} onClose={() => setDeleteCandidate(null)}>
        <div className="font-semibold mb-2">
          ¿Borrar el tipo "{deleteCandidate}"?
        </div>
        <div className="text-sm text-zinc-600 mb-4">
          Los archivos que ya tenían este tipo conservan su etiqueta, pero el
          tipo deja de aparecer en los selectores.
        </div>
        <div className="flex gap-2 justify-end">
          <Button outline onClick={() => setDeleteCandidate(null)}>
            Cancelar
          </Button>
          <Button color="red" onClick={handleConfirmDelete}>
            Borrar
          </Button>
        </div>
      </Alert>
    </div>
  );
};

export default ArchivosTiposManager;
